import { useState, useEffect, useCallback, useMemo } from 'react';
import { Shield } from 'lucide-react';
import { Alert, DataGrid, FilterBar, METRIC_READ_CAPTIONS, MetricCard, MetricCardGrid } from '@aaimplatform/ui';
import type { ActiveFilter, ColumnDef, FilterDefinition } from '@aaimplatform/ui';
import { propertyTitleAdminClient } from '../../api/client';
import { useAuthStore } from '../../stores/auth';
import type { components as PropertyTitleComponents } from '@aaimplatform/institutional-types/property-title-admin';
import {
  Absent,
  EnumBadge,
  GridEmpty,
  GridSkeleton,
  LIEN_RISK_LEVEL,
  ListHeader,
  TAX_LIEN_RISK_LABELS,
  filterOptions,
  formatUsd,
  formatUsdCompact,
  labelFor,
  reportedNumber,
  sumReported,
  tile,
  type ListReadState,
} from './CollateralListParts';

// Canonical types from the deployed property-title-admin service
// (/api/v1/title/lien-verifications). The page was migrated 2026-06-19 off the
// dead contract-documentation stub (/api/lien-verifications) onto this service,
// mirroring the sibling collateral/UCCFilings.tsx consumption pattern.
type LienVerification = PropertyTitleComponents['schemas']['LienVerification'];
type LienRiskLevel = PropertyTitleComponents['schemas']['LienRiskLevel'];

const FILTERS: FilterDefinition[] = [
  { id: 'riskLevel', label: 'Risk level', type: 'select', options: filterOptions(LIEN_RISK_LEVEL) },
];

function isLienRiskLevel(v: string | undefined): v is LienRiskLevel {
  return v != null && Object.prototype.hasOwnProperty.call(LIEN_RISK_LEVEL, v);
}

function formatAddress(address: LienVerification['address'] | undefined): string | null {
  if (!address) return null;
  const line1 = address.street ?? '';
  const cityState = [address.city, address.state].filter(Boolean).join(', ');
  const tail = [cityState, address.zipCode].filter(Boolean).join(' ');
  return [line1, tail].filter(Boolean).join(', ') || null;
}

function RightHeader({ children }: { children: string }) {
  return <span className="block w-full text-right">{children}</span>;
}

/**
 * Lien Verifications Page -- read-only list over the deployed
 * property-title-admin service (/api/v1/title/lien-verifications).
 *
 * One row per property: lien count, total lien exposure, recommended lending
 * position, tax-lien risk, and overall risk level. A verification answers
 * 422 LIEN_DATA_UNAVAILABLE while ATTOM is the only source, because ATTOM
 * carries no recorded liens (ruling of 2026-09-27), so the list is empty until
 * a title-data source is wired. A figure a row does not report renders as
 * absent, never as a zero.
 *
 * The backend serializes totalLienAmount as a string (BigInt -> string for
 * JSON transport); reportedNumber() reads that form, and an absent amount
 * stays absent rather than coercing to 0.
 */
export function LienVerificationsPage() {
  // FI-tenant operators carry a tenantId; platform admins do not, in which
  // case the optional-JWT resolver maps the verified token to the apex tenant
  // (an empty string is treated as absent server-side).
  const tenantId = useAuthStore((s) => s.user?.tenantId) ?? '';
  const [verifications, setVerifications] = useState<LienVerification[]>([]);
  const [read, setRead] = useState<ListReadState>('loading');
  const [error, setError] = useState<string | null>(null);
  const [activeFilters, setActiveFilters] = useState<ActiveFilter[]>([]);

  const riskValue = activeFilters.find((f) => f.filterId === 'riskLevel')?.value;
  const riskFilter = isLienRiskLevel(riskValue) ? riskValue : undefined;

  const fetchVerifications = useCallback(async () => {
    setRead('loading');
    setError(null);
    try {
      const result = await propertyTitleAdminClient.GET('/title/lien-verifications', {
        params: {
          query: {
            tenantId,
            riskLevel: riskFilter,
            limit: 100,
          },
        },
      });
      if (result.error || !result.data) {
        const status = result.response?.status ?? 0;
        throw new Error(`HTTP ${status}: failed to load lien verifications`);
      }
      const body = result.data as { data?: LienVerification[] };
      setVerifications(body.data ?? []);
      setRead('loaded');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load lien verifications');
      setRead('failed');
    }
  }, [tenantId, riskFilter]);

  useEffect(() => {
    void fetchVerifications();
  }, [fetchVerifications]);

  // -------------------------------------------------------------------------
  // Summary metrics. Lien exposure sums only the rows that report a figure.
  // -------------------------------------------------------------------------

  const highRiskCount = verifications.filter(
    (v) => v.riskLevel === 'HIGH' || v.riskLevel === 'CRITICAL'
  ).length;
  const withLiensCount = verifications.filter((v) => {
    const count = reportedNumber(v.lienCount);
    return count != null && count > 0;
  }).length;
  const lienExposure = sumReported(verifications.map((v) => reportedNumber(v.totalLienAmount)));

  const exposureTile = tile(
    read,
    lienExposure == null ? null : formatUsdCompact(lienExposure),
    verifications.length === 0 ? 'No verifications on file' : METRIC_READ_CAPTIONS.notReported
  );

  const columns = useMemo<ColumnDef<LienVerification, unknown>[]>(
    () => [
      {
        id: 'property',
        header: 'Property',
        accessorFn: (v) => formatAddress(v.address) ?? '',
        cell: ({ row }) => {
          const address = formatAddress(row.original.address);
          return address == null ? (
            <Absent />
          ) : (
            <span className="text-sm font-sans font-medium text-primary leading-normal">{address}</span>
          );
        },
        size: 280,
      },
      {
        id: 'lienCount',
        header: () => <RightHeader>Lien Count</RightHeader>,
        accessorFn: (v) => reportedNumber(v.lienCount) ?? undefined,
        cell: ({ row }) => {
          const count = reportedNumber(row.original.lienCount);
          return (
            <span className="block text-right text-sm font-mono text-secondary tabular-nums leading-normal">
              {count == null ? <Absent /> : count}
            </span>
          );
        },
        size: 110,
      },
      {
        id: 'totalLienAmount',
        header: () => <RightHeader>Total Lien Amount</RightHeader>,
        accessorFn: (v) => reportedNumber(v.totalLienAmount) ?? undefined,
        cell: ({ row }) => {
          const amount = reportedNumber(row.original.totalLienAmount);
          return (
            <span className="block text-right text-sm font-mono font-semibold text-primary tabular-nums leading-normal">
              {amount == null ? <Absent /> : formatUsd(amount)}
            </span>
          );
        },
        size: 160,
      },
      {
        id: 'recommendedPosition',
        header: () => <RightHeader>Recommended Position</RightHeader>,
        accessorFn: (v) => reportedNumber(v.recommendedPosition) ?? undefined,
        cell: ({ row }) => {
          const position = reportedNumber(row.original.recommendedPosition);
          return (
            <span className="block text-right text-sm font-mono text-secondary tabular-nums leading-normal">
              {position == null ? <Absent /> : position}
            </span>
          );
        },
        size: 180,
      },
      {
        id: 'taxLienRisk',
        header: 'Tax Lien Risk',
        accessorFn: (v) => v.taxLienRisk ?? '',
        cell: ({ row }) =>
          row.original.taxLienRisk ? (
            <span className="text-sm font-sans text-secondary leading-normal">
              {labelFor(TAX_LIEN_RISK_LABELS, row.original.taxLienRisk)}
            </span>
          ) : (
            <Absent />
          ),
        size: 130,
      },
      {
        id: 'riskLevel',
        header: 'Risk Level',
        accessorFn: (v) => v.riskLevel ?? '',
        cell: ({ row }) => <EnumBadge value={row.original.riskLevel} map={LIEN_RISK_LEVEL} />,
        size: 120,
      },
      {
        id: 'dataSource',
        header: 'Data Source',
        accessorFn: (v) => v.dataSource ?? '',
        cell: ({ row }) =>
          row.original.dataSource ? (
            <span className="text-sm font-sans text-tertiary leading-normal">{row.original.dataSource}</span>
          ) : (
            <Absent />
          ),
        size: 120,
      },
    ],
    []
  );

  return (
    <div>
      <ListHeader
        subtitle={
          <p className="text-sm font-sans text-tertiary">Recorded-lien analysis by property</p>
        }
        onRefresh={() => void fetchVerifications()}
      />

      {error && (
        <Alert type="error" title="Unable to load lien verifications" className="mb-6">
          {error}
        </Alert>
      )}

      <MetricCardGrid className="mb-6">
        <MetricCard label="Total Verifications" {...tile(read, String(verifications.length))} />
        <MetricCard label="High Risk" {...tile(read, String(highRiskCount))} />
        <MetricCard label="With Liens" {...tile(read, String(withLiensCount))} />
        <MetricCard label="Total Lien Exposure" {...exposureTile} />
      </MetricCardGrid>

      <FilterBar
        filters={FILTERS}
        activeFilters={activeFilters}
        onFiltersChange={setActiveFilters}
        className="rounded-t-sm"
      />

      {read === 'loading' ? (
        <GridSkeleton />
      ) : read === 'failed' ? null : verifications.length === 0 ? (
        <GridEmpty
          icon={<Shield className="h-8 w-8" />}
          title={riskFilter ? 'No lien verifications match this risk level' : 'No lien verifications on file'}
          description={
            riskFilter
              ? 'Clear the risk level filter to see every verification.'
              : 'Recorded liens need a title-data source. ATTOM, the only property source connected today, supplies none, so a lien verification cannot run yet.'
          }
        />
      ) : (
        <DataGrid<LienVerification>
          data={verifications}
          columns={columns}
          enablePagination
          defaultPageSize={25}
          enableColumnResize
          hoverable
          striped
          getRowId={(v) => v.id}
          className="rounded-b-sm shadow-sm"
        />
      )}
    </div>
  );
}
